import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { facilitatorSetup, facilitatorVerify } from "@/lib/api";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Something went wrong. Please try again.";
};

export const FacilitatorGate = ({ configured, onUnlocked }) => {
  const [passcode, setPasscode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [setupKey, setSetupKey] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!configured && passcode !== confirm) {
      setError("Passcodes do not match.");
      return;
    }
    setBusy(true);
    try {
      const token = configured ? await facilitatorVerify(passcode) : await facilitatorSetup(passcode, setupKey);
      onUnlocked(token);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
      <div data-testid="facilitator-gate" className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-8 shadow-sm">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <Lock className="h-4 w-4" /> Facilitator access
        </div>
        <h1 className="mt-3 font-heading text-2xl font-semibold text-stone-900">
          {configured ? "Enter your passcode" : "Set a facilitator passcode"}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-stone-500">
          {configured
            ? "Trainee performance data is protected. Enter the facilitator passcode to continue."
            : "This is your first visit. Enter the one-time setup key from your administrator, then choose a passcode of at least 10 characters to protect trainee performance data."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          {!configured && (
            <input
              data-testid="setup-key-input"
              type="password"
              value={setupKey}
              onChange={(e) => setSetupKey(e.target.value)}
              placeholder="One-time setup key"
              aria-label="One-time setup key from your administrator"
              className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800 outline-none focus:border-emerald-500"
            />
          )}
          <input
            data-testid="passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            autoFocus
            className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800 outline-none focus:border-emerald-500"
          />
          {!configured && (
            <input
              data-testid="passcode-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm passcode"
              className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800 outline-none focus:border-emerald-500"
            />
          )}
          {error && (
            <p data-testid="gate-error" className="text-sm text-rose-700" role="alert">{error}</p>
          )}
          <Button
            data-testid="gate-submit"
            type="submit"
            disabled={busy || passcode.length === 0}
            className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white h-11"
          >
            <KeyRound className="mr-1.5 h-4 w-4" />
            {busy ? "Checking…" : configured ? "Unlock dashboard" : "Set passcode and continue"}
          </Button>
        </form>

        <Link to="/" data-testid="gate-back-link" className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors duration-200">
          <ArrowLeft className="h-4 w-4" /> Back to simulator
        </Link>
      </div>
    </div>
  );
};
